import { motion } from "framer-motion";
import clsx from "clsx";

import { View, Text, Button } from "../base";
import { Icon } from "../base/Icon";
import { AgentDeployment } from "../../redux/reducers/agentDeploymentSlice";

interface DeploymentDetailsCardProps {
  deployment: AgentDeployment;
  onCompare?: (deployment: AgentDeployment) => void;
  className?: string;
  getFunctionName?: (environmentId: string, deployment?: AgentDeployment) => string;
  availableDeployments?: AgentDeployment[];
}

const formatDeploymentTypes = (deploymentType: string[]): string => {
  if (!deploymentType || deploymentType.length === 0) {
    return "Unknown";
  }

  return deploymentType.map((type) => type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()).join(", ");
};

export const DeploymentDetailsCard = ({ deployment, onCompare, className, getFunctionName, availableDeployments }: DeploymentDetailsCardProps) => {
  // Calculate number of other deployments available for comparison
  const otherDeploymentsCount = availableDeployments ? availableDeployments.filter((d) => d.id !== deployment.id).length : 0;

  // Check if there's a previous deployment (chronologically before current)
  const hasPreviousDeployment = availableDeployments
    ? (() => {
        const sortedDeployments = availableDeployments.sort((a, b) => new Date(b.deployedAt).getTime() - new Date(a.deployedAt).getTime());
        const currentIndex = sortedDeployments.findIndex((d) => d.id === deployment.id);
        return currentIndex >= 0 && currentIndex < sortedDeployments.length - 1;
      })()
    : false;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "deployed":
        return "text-blue-700 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20";
      case "pending":
        return "text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20";
      case "failed":
        return "text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20";
      default:
        return "text-gray-700 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx("bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 py-2 px-6", className)}
    >
      {/* Header */}
      <View className="flex items-start justify-between mb-6">
        <View className="flex-1">
          <View className="flex items-center gap-3 mb-2">
            <Text className="text-xl font-semibold text-gray-900 dark:text-white">
              {getFunctionName ? getFunctionName(deployment.environmentId, deployment) : deployment.id}
            </Text>
            <View className={clsx("px-2 py-1 rounded-full text-xs font-medium", getStatusColor(deployment.status))}>{deployment.status}</View>
            {deployment.isActive && (
              <View className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-full text-xs font-medium">Active</View>
            )}
          </View>
        </View>
        <View className="flex items-center gap-2">
          {onCompare && otherDeploymentsCount > 0 && hasPreviousDeployment && (
            <Button
              variant="ghost"
              onClick={() => onCompare(deployment)}
            >
              <Icon
                name="account-tree"
                size={16}
                className="mr-1"
              />
              Compare
            </Button>
          )}
        </View>
      </View>

      {/* Deployment Info */}
      <View className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <View className="space-y-4">
          <View>
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1 mr-2">Deployed At</Text>
            <Text className="text-sm text-gray-900 dark:text-white">{new Date(deployment.deployedAt).toLocaleString()}</Text>
          </View>
          <View>
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1 mr-2">Deployment Type</Text>
            <Text className="text-sm text-gray-900 dark:text-white">{formatDeploymentTypes(deployment.deploymentType)}</Text>
          </View>
          <View>
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1 mr-2">Deployed By</Text>
            <Text className="text-sm text-gray-900 dark:text-white">{deployment.deployedBy}</Text>
          </View>
        </View>
        <View className="space-y-4"></View>
      </View>

      {/* STT Configuration */}
      <View className="mb-6">
        <Text className="text-lg font-medium text-gray-900 dark:text-white mb-4 mr-2">STT Configuration</Text>
        <View className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <View>
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2">Model</Text>
            <Text className="text-sm text-gray-900 dark:text-white font-mono">{deployment.sttConfig.sttModel}</Text>
          </View>
          {deployment.sttConfig.sttLanguage && (
            <View>
              <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2">Language</Text>
              <Text className="text-sm text-gray-900 dark:text-white">{deployment.sttConfig.sttLanguage}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Environment Variables */}
      {Object.keys(deployment.environmentVariables).length > 0 && (
        <View className="mb-6">
          <Text className="text-lg font-medium text-gray-900 dark:text-white mb-4">Environment Variables</Text>
          <View className="space-y-2">
            {Object.entries(deployment.environmentVariables).map(([key, value]) => (
              <View
                key={key}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <Text className="text-sm font-medium text-gray-900 dark:text-white font-mono">{key}</Text>
                <Text className="text-sm text-gray-600 dark:text-gray-300 font-mono">{value}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </motion.div>
  );
};
